'use client';

// The colour scheme, kept the same way lib/token-store.ts keeps the password:
// localStorage is an external store, read through useSyncExternalStore so the
// server render and the hydration pass agree (the server never knows the
// system's scheme, so 'system' is what both sides render first).
//
// What's stored here is the *choice* — 'light', 'dark', or 'system' — not the
// resolved scheme. Resolving 'system' against matchMedia, applying the result
// to <html data-theme>, and reacting live to the OS flipping under it is
// app/theme-sync.tsx's job; this module only tracks what was picked, the same
// division token-store draws between holding a value and using it.
//
// Light is the default, and 'system' is now a stored value rather than the
// absence of one. It used to be the other way round: no key meant 'system', so
// a visitor who had never opened the picker got whatever their OS was set to.
// The console is a paper-coloured room by design and that is the face it should
// arrive with, so the fallback here is 'light' and following the OS is something
// you ask for. The cost is real and worth stating: anyone who was relying on the
// old implicit 'system' has to pick it once, because nothing was written down
// that could tell them apart from a first-time visitor.

export type Theme = 'light' | 'dark' | 'system';

const KEY = 'console.theme';

const listeners = new Set<() => void>();

function emit() {
  for (const l of listeners) l();
}

export function subscribe(onChange: () => void): () => void {
  listeners.add(onChange);
  // Fired by *other* tabs; same-tab writes go through emit() below.
  window.addEventListener('storage', onChange);
  return () => {
    listeners.delete(onChange);
    window.removeEventListener('storage', onChange);
  };
}

export function getSnapshot(): Theme {
  const raw = localStorage.getItem(KEY);
  return raw === 'light' || raw === 'dark' || raw === 'system' ? raw : 'light';
}

/** No stored choice exists during SSR, and the default is light. */
export function getServerSnapshot(): Theme {
  return 'light';
}

export function setTheme(theme: Theme): void {
  // All three are written, including 'system'. Removing the key for 'system'
  // was right while absence meant 'system'; now absence means light, so
  // removing it would silently turn the choice into the opposite one.
  localStorage.setItem(KEY, theme);
  emit();
}
